import React, { useCallback, useState } from 'react';
import { View } from 'react-native';
import { Box, Inline } from '@styleseat/ui';
import { colors } from '@styleseat/brand';
import {
  Touchable,
  BodyText,
  Stack,
} from '../../../ui';
import { ChevronIcon } from '../../../ui/Icons';
import { IQuerySuggestion } from './types';
import { IQueryServiceResult } from '../../../../modules/consumer/search/types';

type ExpandableQuerySuggestionProps = {
  suggestionText: React.ReactNode;
  item: IQuerySuggestion;
  subItems?: Omit<IQuerySuggestion, 'subItems'>[];
  onExpand: (item: IQuerySuggestion) => void;
  onItemSelect: (item: IQuerySuggestion) => void;
  onSubItemSelect: (item: IQuerySuggestion) => void;
};

export const ExpandableQuerySuggestion: React.FCWithChildren<ExpandableQuerySuggestionProps> = ({
  suggestionText,
  item,
  subItems,
  onExpand,
  onItemSelect,
  onSubItemSelect,
}) => {
  const [isExpanded, setExpanded] = useState<boolean>(false);

  const handleClick = useCallback(() => {
    if (isExpanded) {
      setExpanded(false);
    } else {
      onExpand(item);
      setExpanded(true);
    }
  }, [
    isExpanded,
    setExpanded,
    onExpand,
    item,
  ]);

  return (
    <Stack
      borderColor={colors.whiteWash}
      borderBottomWidth={1}
      borderStyle="solid"
      paddingY={8}
    >
      <Touchable onClick={handleClick} testID={item.testId}>
        <Inline
          paddingY={8}
          justifyContent="flex-start"
          alignItems="center"
          space={8}
        >
          <BodyText>{suggestionText}</BodyText>
          <Box>
            <ChevronIcon
              width={12}
              height={12}
              direction={isExpanded ? 'down' : 'right'}
              color={colors.metal}
            />
          </Box>
        </Inline>
      </Touchable>
      {!!(isExpanded && subItems) && (
        <Stack
          paddingY={12}
          space={12}
          paddingLeft={16}
          alignItems="flex-start"
        >
          <Touchable onClick={() => onItemSelect(item)}>
            <View>
              <BodyText color={colors.metal}>
                View All
              </BodyText>
            </View>
          </Touchable>
          {subItems.map(subItem => (
            <Touchable onClick={() => onSubItemSelect(subItem)} key={subItem.id}>
              <View>
                <BodyText color={colors.metal}>
                  {(subItem.data as IQueryServiceResult)?.search_query?.query || subItem.text}
                </BodyText>
              </View>
            </Touchable>
          ))}
        </Stack>
      )}
    </Stack>
  );
};
